import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { marked } from 'marked';
import { apiGet, apiPost } from '../api/client';
import type { TermsAndConditionsResponse } from '../types/api';
import AlertMessage from '../components/AlertMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import './ReAcceptTermsPage.css';

export default function ReAcceptTermsPage() {
  const navigate = useNavigate();

  const [terms, setTerms] = useState<TermsAndConditionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiGet<TermsAndConditionsResponse | undefined>('/api/terms/pending');
        if (!data) {
          // Already up to date — go to dashboard
          navigate('/', { replace: true });
          return;
        }
        setTerms(data);
      } catch {
        setError('Unable to load the updated terms. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  async function handleAccept() {
    if (!terms || !accepted) return;
    setSubmitting(true);
    setError('');
    try {
      await apiPost(`/api/terms/${terms.id}/accept`);
      navigate('/', { replace: true });
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to record acceptance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function renderContent() {
    if (!terms) return '';
    return marked(terms.content) as string;
  }

  if (loading) return <LoadingSpinner fullPage />;

  return (
    <div className="auth-page re-accept-page">
      <div className="re-accept-container">
        <div className="re-accept-header">
          <div className="re-accept-logo">
            <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <rect width="32" height="32" rx="6" fill="#1a1a2e" />
              <path d="M16 3 L5 7.5 L5 18 C5 24.5 10 29 16 31 C22 29 27 24.5 27 18 L27 7.5 Z" fill="#a0785a" />
              <polygon points="16,9 11.5,14 13.5,14 10,19.5 13.5,19.5 13.5,23 18.5,23 18.5,19.5 22,19.5 18.5,14 20.5,14" fill="#f8f7f4" />
            </svg>
            <span className="re-accept-brand">Cedar <span className="re-accept-brand-gold">Vault</span></span>
          </div>
          <h1 className="re-accept-title">Updated Terms & Conditions</h1>
          <p className="re-accept-subtitle">
            We've updated our Terms & Conditions (v{terms?.version}). Please review and accept to continue.
          </p>
        </div>

        {error && <AlertMessage type="error" message={error} />}

        {terms && (
          <>
            <div
              ref={contentRef}
              className="re-accept-content"
              onScroll={() => {
                const el = contentRef.current;
                if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
                  setScrolled(true);
                }
              }}
              dangerouslySetInnerHTML={{ __html: renderContent() }}
            />

            {!scrolled && (
              <p className="re-accept-scroll-hint">↓ Please scroll through the terms above to continue</p>
            )}

            <label className="re-accept-checkbox">
              <input
                type="checkbox"
                checked={accepted}
                disabled={!scrolled}
                onChange={e => setAccepted(e.target.checked)}
              />
              <span>I have read and agree to the Terms & Conditions (v{terms.version})</span>
            </label>

            <button
              className="btn btn-primary re-accept-btn"
              onClick={handleAccept}
              disabled={!accepted || submitting}
            >
              {submitting ? 'Accepting…' : 'Accept & Continue'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
